/**
 * @module Geolocation
 * @description Resolves the visitor's geolocation (country and region) and broadcasts it to the theme.
 *
 * On first visit the location is fetched from the ipify API and cached in sessionStorage;
 * subsequent page views within the session reuse the cached value without a network request.
 *
 * Whichever path resolves, a `GeolocationLoadedEvent` (`geolocation:loaded`) is dispatched on
 * `document`. The module's own listener then:
 * - exposes the location globally as `POV.Geolocation`
 * - mirrors it onto `<body>` as `data-geo-country`, `data-geo-region` and `data-geo-ready`
 * - reveals or hides every geo-targeted element (`[data-geo-mode]`, see {@link applyGeoTargets})
 *
 * On network or API failure (ad blockers commonly block ipify) the store's own country is
 * dispatched with an empty region and nothing is cached, so domestic content still appears and
 * the next page view retries the lookup.
 *
 * The Editor > Geolocation preview theme settings (theme editor only) short-circuit both paths:
 * the preview location is dispatched as-is and nothing is fetched or cached.
 *
 * Both values are Liquid-side and travel on the geo targets themselves: blocks/geotarget.liquid
 * prints `data-geo-fallback` and, in the editor, `data-geo-preview-country`/`-region`, and
 * {@link getGeoLocation} reads them from the first target on the page. A page without targets
 * has no consumer for the location, so a missing config there changes nothing.
 *
 * @example
 * // React to the location resolving (fires on every page view, cached or fresh)
 * import { ThemeEvents } from '@theme/events';
 *
 * document.addEventListener(ThemeEvents.geolocationLoaded, (event) => {
 *   const { country, region } = event.detail;
 * });
 *
 * @example
 * // Synchronous access after the event has fired
 * if (POV.Geolocation?.country === 'GB') { ... }
 *
 * @example
 * // Conditional CSS via the <body> attributes
 * body[data-geo-country='US'] .notice { display: block; }
 *
 * @example
 * // Declarative geo targeting in Liquid (blocks/geotarget.liquid, blocks/noticebanner.liquid emit this)
 * <div data-geo-mode="show" data-geo-countries="US" data-geo-regions="Maine,Nevada" hidden>…</div>
 */

import { ThemeEvents, GeolocationLoadedEvent } from '@theme/events';


/** @constant {string} sessionStorage key the resolved location is cached under */
const STORAGE_KEY = 'POV-GEOLOCATION';

/** @constant {string} elements that opt into geo targeting through data-geo-mode/-countries/-regions (blocks/geotarget.liquid) */
const GEO_TARGET_SELECTOR = '[data-geo-mode]';

/** @constant {string} ipify country-lookup endpoint, keyed for this store */
const API_URL = 'https://geo.ipify.org/api/v2/country?apiKey=at_49m0dR2WfLVkEm8D4zLnRBfFTpit7';

// Register the listener before resolving — the sessionStorage path dispatches synchronously
initGeolocationListener();
getGeoLocation();

/**
 * Resolves the visitor's geolocation and dispatches a `GeolocationLoadedEvent`.
 *
 * Uses the sessionStorage cache when present; otherwise fetches from the ipify API
 * and caches the result for the rest of the session. On failure the error is logged
 * and the store's own country is dispatched instead (not cached).
 *
 * The store country and the editor preview come from the first geo target's data
 * attributes (see the module description); this is the only place that reads them.
 *
 * @returns {Promise<void>}
 * @fires GeolocationLoadedEvent
 */
async function getGeoLocation() {
  const config = document.querySelector(GEO_TARGET_SELECTOR)?.dataset || {};

  // Theme editor preview: forced location from theme settings, never cached
  if (config.geoPreviewCountry) {
    document.dispatchEvent(new GeolocationLoadedEvent({ country: config.geoPreviewCountry, region: config.geoPreviewRegion || '' }));
    return;
  }

  // Use sessionStorage location
  const locationFromSession = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
  if (locationFromSession) {
    document.dispatchEvent(new GeolocationLoadedEvent(locationFromSession));
    return;
  }

  // New visit, no cached location, do API call
  try {
    const response = await fetch(API_URL);
    if (!response.ok) throw new Error(`ipify responded ${response.status}`);

    const { location } = await response.json();
    const geolocation = { country: location.country, region: location.region };

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(geolocation));
    document.dispatchEvent(new GeolocationLoadedEvent(geolocation));
  } catch (error) {
    // Blocked or failed lookup: assume the store's own country so domestic content still shows
    console.error('POV: Failed to get geolocation, assuming store country', error);
    document.dispatchEvent(new GeolocationLoadedEvent({ country: config.geoFallback || '', region: '' }));
  }
}

/**
 * Registers the `geolocation:loaded` listener that publishes the resolved location.
 *
 * `applyGeolocation` could be called directly from {@link getGeoLocation}, but it is
 * wired through the event on purpose — to demonstrate how ThemeEvents custom events
 * are listened to anywhere in the theme.
 *
 * Must run before {@link getGeoLocation} — on a sessionStorage cache hit the event
 * dispatches synchronously, so a listener registered later would miss it.
 *
 * @listens ThemeEvents.geolocationLoaded
 */
function initGeolocationListener() {
  document.addEventListener(ThemeEvents.geolocationLoaded, applyGeolocation);

  // Markup re-rendered after the location resolved arrives hidden and needs the pass again:
  // theme editor section reloads and cart updates (cart.js re-renders the drawer contents)
  const reapply = () => POV.Geolocation && applyGeoTargets(POV.Geolocation);
  document.addEventListener('shopify:section:load', reapply);
  document.addEventListener(ThemeEvents.cartUpdated, reapply);

  /**
   * Publishes the resolved geolocation to the rest of the theme.
   *
   * @param {GeolocationLoadedEvent} event
   * @param {Object} event.detail - The resolved location
   * @param {string} event.detail.country - Two-letter country code, e.g. `GB`
   * @param {string} event.detail.region - Region name within the country
   */
  function applyGeolocation(event) {
    const geolocation = event.detail;

    // Expose the resolved geolocation globally
    // This is useful for JavaScript geolocation logic.
    POV.Geolocation = geolocation;

    // Mirror it onto <body> data attributes
    // This is useful for conditional CSS logic.
    document.body.setAttribute('data-geo-country', geolocation.country);
    document.body.setAttribute('data-geo-region', geolocation.region);
    document.body.setAttribute('data-geo-ready', 'true');

    applyGeoTargets(geolocation);
  }
}

/**
 * Reveals each geo-targeted element the visitor qualifies for and hides the rest.
 *
 * Elements opt in with `data-geo-mode="show|hide"`, a comma-separated `data-geo-countries`
 * list of ISO codes and an optional `data-geo-regions` list of region names that narrows the
 * countries. "show" reveals visitors inside the target, "hide" reveals everyone else. Elements
 * should render with the `hidden` attribute so nothing paints before the location is known.
 *
 * @param {Object} geolocation
 * @param {string} geolocation.country - Two-letter country code, e.g. `GB`
 * @param {string} geolocation.region - Region name within the country
 */
function applyGeoTargets(geolocation) {
  document.querySelectorAll(GEO_TARGET_SELECTOR).forEach(element => {
    element.toggleAttribute('hidden', !matchesGeoTarget(element, geolocation));
  });
}

/**
 * @param {HTMLElement} element - Element carrying the data-geo-* attributes
 * @param {Object} geolocation - Resolved location, see {@link applyGeoTargets}
 * @returns {boolean} Whether the element should be visible for this visitor
 */
function matchesGeoTarget(element, geolocation) {
  const countries = parseList(element.getAttribute('data-geo-countries'));
  const regions = parseList(element.getAttribute('data-geo-regions'));

  let inTarget = countries.includes(normalize(geolocation.country));
  if (inTarget && regions.length > 0) {
    inTarget = regions.includes(normalize(geolocation.region));
  }

  return element.getAttribute('data-geo-mode') === 'show' ? inTarget : !inTarget;
}

function parseList(value) {
  return (value || '').split(',').map(normalize).filter(Boolean);
}

function normalize(value) {
  return String(value || '').trim().toLowerCase();
}
